import React, { useCallback, useState, useEffect, useRef } from "react";

import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";
import { Grid, Typography } from "@mui/material";
import styled from "styled-components";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";

const CustomDatePicker = styled(DatePicker)`
  input {
    text-align: left;
    font: normal normal 500 12px 'Open Sans';
    padding: 8px 8px;
    font-size: 12px;
    letter-spacing: 0px;
    color: black;
    opacity: 1;
    align-items: center;
  }
`;

const StyledCalendarIcon = styled(CalendarTodayOutlinedIcon)`
  && {
    font-size: 18px; /* Adjust the icon size as needed */
    color: #007842;
  }
`;



export default function BasicDatePicker({
  onDateChange,
  selectedTab,
  year,
  quarter,
}) {

  const incrementMap = {
    Daily: { unit: "day", amount: 92 },
    Weekly: { unit: "week", amount: 92 },
    Monthly: { unit: "month", amount: 92 },
  };


  const getQuarterStartDate = useCallback(() => {
    switch (quarter) {
      case "Q1":
        return dayjs(`${year}-01-01`);
      case "Q2":
        return dayjs(`${year}-04-01`);
      case "Q3":
        return dayjs(`${year}-07-01`);
      case "Q4":
        return dayjs(`${year}-10-01`);
      default:
        return dayjs();
    }
  }, [year, quarter]);

  const getQuarterEndDate = useCallback(() => {
    switch (quarter) {
      case "Q1":
        return dayjs(`${year}-03-31`);
      case "Q2":
        return dayjs(`${year}-06-30`);
      case "Q3":
        return dayjs(`${year}-09-30`);
      case "Q4":
        return dayjs(`${year}-12-31`);
      default:
        return dayjs();
    }
  }, [year, quarter]);

  const [value, setValue] = useState(getQuarterStartDate());
  const [value2, setValue2] = useState(getQuarterEndDate());

  useEffect(() => {
    setValue(getQuarterStartDate());
    setValue2(getQuarterEndDate());
  }, [getQuarterStartDate, getQuarterEndDate]);

  const calculateMaxEndDateBasedOnTab = useCallback(() => {
    if (!value) return getQuarterEndDate();

    const { unit, amount } = incrementMap[selectedTab] || { unit: "day", amount: 92 };
    const maxEndDateFromStart = dayjs(value).add(amount, unit);
    const quarterEndDate = getQuarterEndDate();

    return maxEndDateFromStart.isBefore(quarterEndDate) ? maxEndDateFromStart : quarterEndDate;
  }, [value, selectedTab, getQuarterEndDate]);

  useEffect(() => {
    const currentEndDate = dayjs(value2);
    const maxEndDate = calculateMaxEndDateBasedOnTab();
   
    const allowedMaxEndDate = dayjs(value).add(incrementMap[selectedTab]?.amount || 92, incrementMap[selectedTab]?.unit || 'day');
    const isEndDateWithinAllowedRange = currentEndDate.isBefore(allowedMaxEndDate) || currentEndDate.isSame(allowedMaxEndDate, 'day');
    const isEndDateDifferent = !currentEndDate.isSame(maxEndDate, 'day');
    if ((!isEndDateWithinAllowedRange || currentEndDate.isAfter(maxEndDate)) && isEndDateDifferent) {
      setValue2(maxEndDate);
      onDateChange(dayjs(value).format("YYYY-MM-DD"), dayjs(maxEndDate).format("YYYY-MM-DD"));
    } else if (isEndDateWithinAllowedRange && !currentEndDate.isSame(dayjs(value2), 'day')) {
      onDateChange(dayjs(value).format("YYYY-MM-DD"), dayjs(value2).format("YYYY-MM-DD"));
    }
  }, [value, value2, selectedTab, calculateMaxEndDateBasedOnTab, onDateChange]);
  
  

  const handleDateChange = (newValue) => {
    const quarterEndDate = getQuarterEndDate();
    const currentEndDate = dayjs(value2);
    let newEndDate = quarterEndDate;
    const isStartDateAfterCurrentEnd = newValue.isAfter(currentEndDate);
    const gap = currentEndDate.diff(newValue, incrementMap[selectedTab]?.unit || 'day');

    if (isStartDateAfterCurrentEnd || gap >= 92) {
      let potentialNewEndDate = newValue.add(92, incrementMap[selectedTab]?.unit || 'day');
      newEndDate = potentialNewEndDate.isBefore(quarterEndDate) ? potentialNewEndDate : quarterEndDate;
    } else {
      newEndDate = currentEndDate;
    }

    setValue(newValue);
    setValue2(newEndDate);
    onDateChange(dayjs(newValue).format("YYYY-MM-DD"), dayjs(newEndDate).format("YYYY-MM-DD"));
  };


  const handleDateChange2 = (newEndDate) => {
    if (newEndDate.isBefore(value) || newEndDate.isSame(value)) {
      setValue2(newEndDate);
      const { unit } = incrementMap[selectedTab] || { unit: "day" };
      let newStartDate = newEndDate.subtract(92, unit);
      setValue(newStartDate);
      onDateChange(dayjs(newStartDate).format("YYYY-MM-DD"), dayjs(newEndDate).format("YYYY-MM-DD"));
    } else if (newEndDate.diff(value, incrementMap[selectedTab]?.unit || 'day') > 92) {
      let newStartDate = newEndDate.subtract(92, incrementMap[selectedTab]?.unit || 'day');
      setValue(newStartDate);
      setValue2(newEndDate);
      onDateChange(dayjs(newStartDate).format("YYYY-MM-DD"), dayjs(newEndDate).format("YYYY-MM-DD"));
    } else {
      setValue2(newEndDate);
      onDateChange(dayjs(value).format("YYYY-MM-DD"), dayjs(newEndDate).format("YYYY-MM-DD"));
    }
  };


  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Grid container spacing={1} style={{ padding: "8px 18px 8px 8px" }}>
        <Grid item xs={6}>
          <Typography variant="body1" style={{ fontFamily: 'Open Sans', fontSize: '12px', color: 'black' }}>Start Date</Typography>
          <CustomDatePicker
           views={['year', 'month', 'day']}
            value={value}
            onChange={handleDateChange}
            shouldDisableDate={(date) => date.isSame(value2, 'day')}
            maxDate={getQuarterEndDate().subtract(1, 'day')}
            format="DD/MM/YYYY"
            renderInput={(params) => <TextField {...params} />}
            placeholder="Select the Start Date"
          />
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body1" style={{ fontFamily: 'Open Sans', fontSize: '12px', color: 'black' }}>End Date</Typography>
          <CustomDatePicker
           views={['year', 'month', 'day']}
            value={value2}
            onChange={handleDateChange2}
            shouldDisableDate={(date) => date.isSame(value, 'day')}
            maxDate={getQuarterEndDate()}

            format="DD/MM/YYYY"
            renderInput={(params) => <TextField {...params} />}
            placeholder="Select the End Date"
          />
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
}






